import { FILLABLE_TOOLS, type ToolMode, usesBrushSize } from "#canvas/types.ts";
import { BRUSH_SIZE_VALUES, COLS, PALETTE, ROWS, SECTION, SECTION_H, TOOLS } from "#puf/constants.ts";
import { store } from "#puf/store/index.ts";
import { useSelector } from "@xstate/store-react";
import { HexColorPicker } from "react-colorful";

function toolLabel(mode: ToolMode): string {
  return mode.kind === "picker" ? "PICKER" : mode.brush;
}

function sameMode(a: ToolMode, b: ToolMode): boolean {
  if (a.kind === "picker") return b.kind === "picker";
  return b.kind === "brush" && a.brush === b.brush;
}

function usesShapeFill(mode: ToolMode): boolean {
  return mode.kind === "brush" && FILLABLE_TOOLS.has(mode.brush);
}

export function Toolsbar() {
  const mode = useSelector(store, (s) => s.context.mode);
  const brushSize = useSelector(store, (s) => s.context.brushSize);
  const color = useSelector(store, (s) => s.context.color);
  const onion = useSelector(store, (s) => s.context.onion);
  const onionOpacity = useSelector(store, (s) => s.context.onionOpacity);
  const shapeFill = useSelector(store, (s) => s.context.shapeFill);
  const activeFrame = useSelector(store, (s) => s.context.activeFrame);
  const frameCount = useSelector(store, (s) => s.context.frames.length);

  const showShapeFill = usesShapeFill(mode);
  const showBrushSize = usesBrushSize(mode, shapeFill);
  const sizeLabel = showShapeFill ? "Border Width" : "Brush Size";

  return (
    <aside className="border-b border-line bg-bg-2 lg:border-b-0 lg:border-r lg:overflow-y-auto lg:col-start-1 lg:row-span-2">
      <div className={SECTION}>
        <h3 className={SECTION_H}>Tools</h3>
        <div className="grid grid-cols-3 gap-1.5">
          {TOOLS.map((t) => {
            const active = sameMode(mode, t.mode);
            const key = t.mode.kind === "picker" ? "picker" : `brush:${t.mode.brush}`;
            return (
              <button
                key={key}
                onClick={() => {
                  store.trigger["mode.set"]({ mode: t.mode });
                }}
                aria-label={t.label}
                aria-pressed={active}
                className={`bg-bg-3 border text-text px-1 py-3.5 rounded-sm cursor-pointer flex flex-col items-center gap-1 text-[10px] ${
                  active
                    ? "bg-[color-mix(in_srgb,var(--color-accent)_22%,var(--color-bg-3))] border-accent"
                    : "border-line"
                }`}
              >
                <svg
                  aria-hidden
                  className="w-4.5 h-4.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={t.icon} />
                </svg>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {showBrushSize && (
        <div className={SECTION}>
          <h3 className={SECTION_H}>{sizeLabel}</h3>
          <div className="flex gap-1 mt-2">
            {BRUSH_SIZE_VALUES.map((s) => {
              const active = brushSize === s;
              return (
                <button
                  key={s}
                  onClick={() => {
                    store.trigger["brushSize.set"]({ size: s });
                  }}
                  aria-label={`brush size ${s}`}
                  aria-pressed={active}
                  className={`flex-1 py-1 rounded-[3px] cursor-pointer border ${
                    active ? "bg-accent text-bg border-accent" : "bg-bg-3 border-line text-text"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showShapeFill && (
        <div className={SECTION}>
          <h3 className={SECTION_H}>Fill</h3>
          <div className="flex gap-1.5 mt-2">
            <button
              onClick={() => {
                store.trigger["shapeFill.set"]({ on: false });
              }}
              aria-pressed={!shapeFill}
              className={`flex-1 py-1 rounded-[3px] cursor-pointer border text-[11px] ${
                !shapeFill ? "bg-accent text-bg border-accent font-semibold" : "bg-bg-3 border-line text-text"
              }`}
            >
              Outline
            </button>
            <button
              onClick={() => {
                store.trigger["shapeFill.set"]({ on: true });
              }}
              aria-pressed={shapeFill}
              className={`flex-1 py-1 rounded-[3px] cursor-pointer border text-[11px] ${
                shapeFill ? "bg-accent text-bg border-accent font-semibold" : "bg-bg-3 border-line text-text"
              }`}
            >
              Filled
            </button>
          </div>
        </div>
      )}

      <div className={SECTION}>
        <h3 className={SECTION_H}>Color</h3>
        <div className="flex items-center gap-2.5 mb-2.5">
          <div className="w-9 h-9 rounded-sm border border-line" style={{ background: color }} />
          <div className="flex-1">
            <div className="text-[13px] font-semibold">{color.toUpperCase()}</div>
            <div className="text-dim text-[10px]">HEX</div>
          </div>
        </div>
        <HexColorPicker
          color={color}
          onChange={(c) => {
            store.trigger["color.set"]({ color: c });
          }}
          style={{ width: "100%", height: 140 }}
        />
        <div className="grid grid-cols-8 gap-1 mt-2">
          {PALETTE.map((c) => (
            <button
              key={c}
              onClick={() => {
                store.trigger["color.set"]({ color: c });
              }}
              aria-label={c}
              className={`aspect-square rounded-[3px] border border-line cursor-pointer p-0 ${
                c.toLowerCase() === color.toLowerCase() ? "outline-2 outline-accent" : ""
              }`}
              style={{ background: c }}
            />
          ))}
        </div>
      </div>

      <div className={SECTION}>
        <h3 className={SECTION_H}>Onion</h3>
        <div className="flex gap-2 items-center mb-1.5">
          <button
            onClick={() => {
              store.trigger["onion.toggle"]();
            }}
            aria-pressed={onion}
            className={`px-2.5 py-1 rounded-[3px] cursor-pointer text-[10px] font-semibold tracking-wider border ${
              onion ? "bg-accent text-bg border-accent" : "bg-bg-3 text-dim border-line"
            }`}
          >
            {onion ? "ON" : "OFF"}
          </button>
          <span className="text-dim text-[10px]">ghost {Math.round(onionOpacity * 100)}%</span>
        </div>
        <input
          type="range"
          min={0.05}
          max={0.8}
          step={0.05}
          value={onionOpacity}
          onChange={(e) => {
            store.trigger["onionOpacity.set"]({ value: Number(e.target.value) });
          }}
          disabled={!onion}
          className="w-full"
        />
      </div>

      <div className={SECTION}>
        <h3 className={SECTION_H}>Canvas</h3>
        <div className="text-[11px]">
          <Row label="Size" value={`${COLS} × ${ROWS}`} />
          <Row label="Frame" value={`${activeFrame + 1} / ${frameCount}`} />
          <Row label="Tool" value={toolLabel(mode)} />
        </div>
      </div>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-0.5 text-dim">
      <span>{label}</span>
      <span className="text-text">{value}</span>
    </div>
  );
}
